import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/shared";
import { api, ago, errMsg, label } from "@/lib/api";

const TYPES = [["workforce_board", "Workforce board"], ["college", "College career office"], ["community", "Community job page"], ["library", "Public library"], ["nonprofit", "Nonprofit"], ["other", "Other"]];
const EMPTY = { org: "", contact_name: "", email: "", place: "", org_type: "workforce_board" };

const Field = ({ k, text, f, set, ...rest }) => (
  <label className="block text-sm font-medium">{text}
    <Input className="mt-1" value={f[k]} onChange={(e) => set({ ...f, [k]: e.target.value })} data-testid={`outreach-${k}`} {...rest} /></label>
);

export default function AdminOutreach() {
  const [d, setD] = useState(null);
  const [places, setPlaces] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [postal, setPostal] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/outreach").then((r) => { setD(r.data); setPostal((p) => p || r.data.postal_address); });
  useEffect(() => {
    load();
    api.get("/public/places").then((r) => setPlaces([...r.data.places, ...r.data.cities]));
  }, []);
  const send = async () => {
    setBusy(true);
    try { await api.post("/admin/outreach", { ...f, postal_address: postal }); toast.success(`Invitation sent to ${f.org}`); setF({ ...EMPTY, place: f.place, org_type: f.org_type }); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return <p className="text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="admin-outreach">
      <Panel testid="outreach-form">
        <h3 className="font-semibold">Invite a local partner to use the free jobs widget</h3>
        <p className="mt-1 text-sm text-muted-foreground">Add a workforce board, college career office or community page you found on their public website. Each address gets one email per 30 days, includes a one-click opt-out, and is limited to {d.daily_cap} a day. Sent today: <span data-testid="outreach-sent-today">{d.sent_today}</span> · opted out: {d.opted_out}</p>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field k="org" text="Organization" f={f} set={setF} placeholder="e.g. Workforce Solutions of Central Texas" />
          <Field k="contact_name" text="Contact name (optional)" f={f} set={setF} />
          <Field k="email" text="Contact email" f={f} set={setF} type="email" />
          <label className="block text-sm font-medium">Area for their widget
            <select className="mt-1 block w-full rounded-md border bg-white p-2 text-sm" value={f.place} onChange={(e) => setF({ ...f, place: e.target.value })} data-testid="outreach-place">
              <option value="">Choose…</option>{places.map((p) => <option key={p.slug} value={p.slug}>{p.kind === "city" ? `${p.name}, ${p.state}` : p.name}</option>)}</select></label>
          <label className="block text-sm font-medium">Type
            <select className="mt-1 block w-full rounded-md border bg-white p-2 text-sm" value={f.org_type} onChange={(e) => setF({ ...f, org_type: e.target.value })} data-testid="outreach-org-type">
              {TYPES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></label>
          <label className="block text-sm font-medium">Your business mailing address (required by anti-spam law)
            <Input className="mt-1" value={postal} onChange={(e) => setPostal(e.target.value)} data-testid="outreach-postal" placeholder="Street, city, state/province, ZIP" /></label>
        </div>
        <Button className="mt-4 rounded-full" disabled={busy || !f.org || !f.email || !f.place || postal.length < 10} onClick={send} data-testid="outreach-send">Send invitation</Button>
      </Panel>
      <Panel testid="outreach-log">
        <h3 className="mb-3 font-semibold">Sent invitations</h3>
        {d.items.length === 0 ? <p className="text-sm text-muted-foreground">None yet.</p> : d.items.map((x) => (
          <div key={x.outreach_id} className="flex justify-between gap-3 border-b py-2 text-sm last:border-0" data-testid={`outreach-row-${x.outreach_id}`}>
            <span className="min-w-0 truncate">{x.org} · {x.email} · {x.area}</span><span className="shrink-0 text-xs text-muted-foreground">{label(x.status)} · {ago(x.created_at)}</span></div>))}
      </Panel>
    </div>
  );
}
